/**
 * Sync Monitoring Store
 * Zustand store for the sync status and its real-time updates
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { streamUrl } from '@/platform/api/stream-ticket'
import { storageService } from '@/shared/services/storage.service'
import { logger } from '@/shared/utils/logger'

import {
  reduceSyncProgressEvent,
  reduceSyncStartedEvent,
  type PartialSseSyncEvent,
} from './syncEventReducers'
import { syncApi } from '../api/syncApi'
import { pruneAcceptedJobs, type AcceptedImportJob } from './accepted-jobs'
import type { SyncStatus, AutoRefreshInterval, SseSyncEvent } from '../types'

interface MonitoringStore {
  // State
  syncStatus: SyncStatus | null
  /** Jobs accepted by a 202 that no snapshot has judged yet (see `pruneAcceptedJobs`). */
  acceptedJobs: Record<string, AcceptedImportJob>
  /**
   * Local time (epoch ms) the request that produced `syncStatus` LEFT. A job
   * learned after it cannot be judged "gone" by this snapshot.
   */
  statusRequestedAt: number | null
  isStatusLoading: boolean
  statusError: string | null

  // SSE connection
  isConnected: boolean
  isConnecting: boolean
  reconnectAttempts: number
  sseConnection: EventSource | null
  // Tracked so disconnectSse() can cancel a pending exponential-backoff retry.
  reconnectTimeoutId: ReturnType<typeof setTimeout> | null

  // Settings
  autoRefreshEnabled: boolean
  autoRefreshInterval: AutoRefreshInterval
  soundNotifications: boolean

  // Actions - Status
  fetchSyncStatus: () => Promise<void>
  /** Show a job the server just accepted, before any snapshot lists it. */
  registerAcceptedJob: (job: Omit<AcceptedImportJob, 'acceptedAt'>) => void

  // Actions - Notifications. Internal to the SSE handlers below — invoked
  // when soundNotifications is enabled and a sync_completed/sync_failed
  // event arrives.
  playNotificationSound?: () => void

  // Actions - SSE event application. Shared by the store's own connectSse()
  // listener AND external consumers of the shared `syncClient` singleton
  // (ImportProgressIndicator on /import never calls connectSse(), so it
  // pipes syncClient 'sync_progress' events through this action instead).
  applySyncProgressEvent: (event: SseSyncEvent) => void

  // Actions - SSE connection
  connectSse: () => void
  disconnectSse: () => void

  // Actions - Settings
  setAutoRefreshInterval: (interval: AutoRefreshInterval) => void
}

// Exponential backoff config for SSE reconnection. Native EventSource auto-
// reconnects with the original URL (and thus a stale token) → infinite 401 if
// the JWT expires. We handle reconnects ourselves so the new request re-reads
// the auth token from storage every time.
/** Bumped by every connectSse()/disconnectSse(): a ticket request that resolves after a newer one is stale. */
let sseAttempt = 0

/** The status request in flight, and whether a caller asked for a re-read meanwhile. */
let statusFetchInFlight: Promise<void> | null = null
let statusRefetchRequested = false

const SSE_RECONNECT_BASE_MS = 1000
const SSE_RECONNECT_CAP_MS = 30_000
const SSE_RECONNECT_MAX_ATTEMPTS = 10

/**
 * Decode the current user's tenantId + role from the JWT in localStorage.
 *
 * Round 2 — FIX D: backend filters cross-tenant SSE events server-side, but
 * a small race exists when a user is demoted from super_admin mid-stream
 * (role is re-validated once per 2s poll tick). This client-side filter is
 * the belt-and-braces guard.
 *
 * The frontend `UserResponse` type intentionally OMITS `tenantId`, and there
 * is no Zustand auth store — `AuthContext` lives in React and is not
 * reachable from Zustand actions. The JWT in `localStorage` is the cheapest
 * source of both claims without plumbing context through the store.
 *
 * Failure mode: a malformed token simply yields `{ tenantId: undefined,
 * role: undefined }`, which causes the filter to be permissive (drops
 * nothing). That's safe because the server-side check is still active.
 */
function getCurrentAuthClaims(): { tenantId: string | undefined; role: string | undefined } {
  try {
    const token = storageService.getAuthToken()
    if (!token) return { tenantId: undefined, role: undefined }
    const parts = token.split('.')
    if (parts.length < 2 || !parts[1]) return { tenantId: undefined, role: undefined }
    // base64url → base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    // Pad with '=' to a length divisible by 4
    const padded = base64 + '==='.slice((base64.length + 3) % 4)
    const json = atob(padded)
    const payload = JSON.parse(json) as { tenantId?: string; role?: string }
    return { tenantId: payload.tenantId, role: payload.role }
  } catch {
    return { tenantId: undefined, role: undefined }
  }
}

/**
 * Round 2 — FIX D: belt-and-braces cross-tenant event filter. Returns
 * `true` when the event should be dropped (caller's tenant doesn't match
 * the event's tenantId, and the caller is not a super_admin). Returns
 * `false` if the event is in-scope OR we can't determine scope (permissive
 * fallback — server-side filter is the real guard).
 */
function shouldDropCrossTenantEvent(eventTenantId: string | undefined | null): boolean {
  if (!eventTenantId) return false // Backend didn't tag — let it through
  const { tenantId, role } = getCurrentAuthClaims()
  if (!tenantId || !role) return false // Can't tell — be permissive
  if (role === 'super_admin') return false // Cross-tenant is intentional
  return eventTenantId !== tenantId
}

export const useMonitoringStore = create<MonitoringStore>()(
  persist(
    (set, get) => ({
      // Initial state
      syncStatus: null,
      acceptedJobs: {},
      statusRequestedAt: null,
      isStatusLoading: false,
      statusError: null,
      isConnected: false,
      isConnecting: false,
      reconnectAttempts: 0,
      sseConnection: null,
      reconnectTimeoutId: null,
      autoRefreshEnabled: true,
      autoRefreshInterval: 5,
      soundNotifications: false,

      // Fetch sync status. ONE request at a time: a call while one is in
      // flight asks for a single trailing re-read instead of a parallel one.
      // Parallel fetches (a bulk sync fired one per source) could land out of
      // order and put an OLDER snapshot on screen after a newer one.
      fetchSyncStatus: () => {
        if (statusFetchInFlight) {
          statusRefetchRequested = true
          return statusFetchInFlight
        }
        const run = async (): Promise<void> => {
          do {
            statusRefetchRequested = false
            const requestedAt = Date.now()
            set({ isStatusLoading: true, statusError: null })
            try {
              const status = await syncApi.getStatus()
              // ONE set: the snapshot and the optimistic rows it judged change
              // together, so no render shows a job twice or not at all.
              set({
                syncStatus: status,
                acceptedJobs: pruneAcceptedJobs(get().acceptedJobs, requestedAt),
                statusRequestedAt: requestedAt,
                isStatusLoading: false,
              })
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Failed to fetch sync status'
              set({ statusError: message, isStatusLoading: false })
            }
          } while (statusRefetchRequested)
        }
        statusFetchInFlight = run().finally(() => {
          statusFetchInFlight = null
        })
        return statusFetchInFlight
      },

      registerAcceptedJob: (job) => {
        set({
          acceptedJobs: { ...get().acceptedJobs, [job.jobId]: { ...job, acceptedAt: Date.now() } },
        })
      },

      // Apply a parsed `sync_progress` SSE event to the matching activeSyncs
      // entry. Single reducer for BOTH SSE paths: the connectSse() listener
      // below (admin SyncMonitoring page) and syncClient subscribers
      // (ImportProgressIndicator on /import) — keeping the cross-tenant
      // guard and stage-preservation behavior identical everywhere.
      applySyncProgressEvent: (event) => {
        if (shouldDropCrossTenantEvent(event.tenantId)) return
        const currentStatus = get().syncStatus
        if (!currentStatus) return
        // Merge rules (partial-event field preservation + monotonic
        // per-card progress guard) live in the pure reducer so they are
        // unit-testable and identical for every SSE path.
        set({ syncStatus: reduceSyncProgressEvent(currentStatus, event) })
      },

      // Connect SSE
      connectSse: () => {
        const state = get()
        // Guard only on the actual connection. `isConnecting` alone is not safe:
        // React StrictMode mounts/unmounts the effect twice in dev, and the
        // cleanup used to leave `isConnecting=true` orphaned forever, blocking
        // every subsequent attempt.
        if (state.sseConnection) {
          return
        }

        // Cancel any pending exponential-backoff retry — an explicit connect
        // supersedes a scheduled reconnect so we don't end up with two streams.
        if (state.reconnectTimeoutId !== null) {
          clearTimeout(state.reconnectTimeoutId)
          set({ reconnectTimeoutId: null })
        }

        set({ isConnecting: true })

        const attempt = ++sseAttempt
        void (async () => {
          try {
            // EventSource cannot send custom Authorization headers, so the stream authenticates with a
            // single-use TICKET (`platform/api/stream-ticket.ts`) — never the JWT in the URL. A new ticket is
            // requested on EVERY (re)connect: it is spent by the first connection, and asking again also picks
            // up a refreshed session after a backoff retry.
            const url = await streamUrl('/api/imports/sync/stream', 'sync-stream')
            // A newer connectSse() or a disconnectSse() happened while the ticket was in flight: this attempt
            // is stale and must not open a second stream.
            if (attempt !== sseAttempt) return
            if (!url) throw new Error('No stream ticket')
            const eventSource = new EventSource(url, {
              withCredentials: true,
            })

            eventSource.addEventListener('connected', (_e) => {
              // Successful connection resets the backoff counter so the next
              // disconnect starts retrying from 1s again.
              set({ isConnected: true, isConnecting: false, reconnectAttempts: 0 })
            })

            eventSource.addEventListener('sync_started', (e) => {
              // The job-manager relay maps lifecycle 'started'/'retried' onto
              // sync_started with payloads lacking startedAt/sourceName/type —
              // parse as a partial so the reducer is forced to default every
              // field through the existing card (a clobbered startedAt used to
              // crash formatDistanceToNow with a RangeError downstream).
              const data = JSON.parse(e.data) as PartialSseSyncEvent
              if (shouldDropCrossTenantEvent(data.tenantId)) return
              const currentStatus = get().syncStatus
              if (!currentStatus) return
              set({ syncStatus: reduceSyncStartedEvent(currentStatus, data) })
            })

            eventSource.addEventListener('sync_progress', (e) => {
              // Full application logic (cross-tenant guard + stage
              // preservation) lives in the shared store action above.
              get().applySyncProgressEvent(JSON.parse(e.data) as SseSyncEvent)
            })

            // Each completed/failed transition fires three times: the worker
            // callback, the 2s backend poll, and (for stuck jobs) the reaper.
            // Without dedup, each one triggers fetchSyncStatus → 3 BD round-trips
            // per job. Track recent (jobId, eventType) pairs for 1.5s and ignore
            // re-emissions during that window.
            const recentTerminal = new Map<string, number>()
            const dedupKey = (jobId: string, ev: string) => `${ev}:${jobId}`
            const isDuplicate = (jobId: string, ev: string): boolean => {
              const key = dedupKey(jobId, ev)
              const now = Date.now()
              const last = recentTerminal.get(key)
              recentTerminal.set(key, now)
              // Garbage-collect entries older than 5s
              for (const [k, t] of recentTerminal) {
                if (now - t > 5000) recentTerminal.delete(k)
              }
              return last !== undefined && now - last < 1500
            }

            eventSource.addEventListener('sync_completed', (e) => {
              const data = JSON.parse(e.data) as SseSyncEvent
              if (shouldDropCrossTenantEvent(data.tenantId)) return
              if (isDuplicate(data.jobId, 'completed')) return
              if (get().soundNotifications) {
                get().playNotificationSound?.()
              }
              get().fetchSyncStatus()
            })

            eventSource.addEventListener('sync_failed', (e) => {
              const data = JSON.parse(e.data) as SseSyncEvent
              if (shouldDropCrossTenantEvent(data.tenantId)) return
              if (isDuplicate(data.jobId, 'failed')) return
              if (get().soundNotifications) {
                get().playNotificationSound?.()
              }
              get().fetchSyncStatus()
            })

            // Round 2 — FIX C: backend emits `role_changed` and closes the
            // stream when the user's role changes mid-flight (e.g. super_admin
            // → user). Force a fresh status fetch under the new role's RLS
            // scope so the UI doesn't keep showing stale cross-tenant data.
            // The backend closes the socket right after, which will trigger
            // our `error` handler and the normal exponential-backoff
            // reconnect (with the now-tenant-scoped policy).
            eventSource.addEventListener('role_changed', () => {
              logger.warn('SSE role_changed received — refreshing status under new role')
              set({ syncStatus: null })
              void get().fetchSyncStatus()
            })

            eventSource.addEventListener('error', (e) => {
              // EventSource fires `error` for both transient drops and permanent
              // failures. Logging this at error level pollutes consoles on every
              // page that mounts ImportProgressIndicator (e.g. /admin/system/*).
              // Downgrade to debug — the reconnect logic below handles recovery.
              logger.debug('SSE error (will reconnect):', e)

              // Close the failed connection ourselves. Native EventSource would
              // otherwise auto-reconnect with the original URL (stale token →
              // infinite 401) — by closing we take control of reconnection and
              // can re-read the auth token on each retry.
              try {
                eventSource.close()
              } catch (_closeErr) {
                // Closing an already-closed EventSource is harmless; ignore.
              }

              const current = get()
              // If we've already scheduled a reconnect, don't stack another.
              if (current.reconnectTimeoutId !== null) {
                return
              }

              const nextAttempt = current.reconnectAttempts + 1
              set({
                sseConnection: null,
                isConnected: false,
                isConnecting: false,
                reconnectAttempts: nextAttempt,
              })

              if (nextAttempt > SSE_RECONNECT_MAX_ATTEMPTS) {
                logger.warn('SSE reconnect: max attempts reached, giving up')
                return
              }

              // Exponential backoff: 1s, 2s, 4s … capped at 30s.
              const delayMs = Math.min(
                SSE_RECONNECT_BASE_MS * Math.pow(2, nextAttempt - 1),
                SSE_RECONNECT_CAP_MS,
              )
              const timeoutId = setTimeout(() => {
                set({ reconnectTimeoutId: null })
                get().connectSse()
              }, delayMs)
              set({ reconnectTimeoutId: timeoutId })
            })

            set({ sseConnection: eventSource })
          } catch (_error) {
            if (attempt !== sseAttempt) return
            set({ isConnecting: false, statusError: 'Failed to connect to sync stream' })
            // No ticket (offline, session expiring): retry like any dropped stream, within the same limit.
            const current = get()
            if (
              current.reconnectTimeoutId === null &&
              current.reconnectAttempts < SSE_RECONNECT_MAX_ATTEMPTS
            ) {
              set({
                reconnectAttempts: current.reconnectAttempts + 1,
                reconnectTimeoutId: setTimeout(() => {
                  set({ reconnectTimeoutId: null })
                  get().connectSse()
                }, SSE_RECONNECT_CAP_MS),
              })
            }
          }
        })()
      },

      // Disconnect SSE
      disconnectSse: () => {
        sseAttempt += 1 // invalidates a ticket request still in flight
        const { sseConnection, reconnectTimeoutId } = get()
        if (sseConnection) {
          sseConnection.close()
        }
        // Cancel any pending exponential-backoff retry so an explicit
        // disconnect (logout, unmount) stays disconnected.
        if (reconnectTimeoutId !== null) {
          clearTimeout(reconnectTimeoutId)
        }
        // Reset isConnecting symmetrically — otherwise a unmount-during-connect
        // leaves the store in `isConnecting=true, sseConnection=null` which
        // blocks future connectSse() calls (see guard above).
        set({
          sseConnection: null,
          isConnected: false,
          isConnecting: false,
          reconnectTimeoutId: null,
          reconnectAttempts: 0,
        })
      },

      // Set auto-refresh interval
      setAutoRefreshInterval: (interval) => {
        set({ autoRefreshInterval: interval, autoRefreshEnabled: interval > 0 })
      },

      // Play notification sound — invoked from the SSE sync_completed /
      // sync_failed handlers when soundNotifications is enabled.
      playNotificationSound: () => {
        const audio = new Audio('/sounds/notification.mp3')
        audio.play().catch(() => {
          // Ignore errors (browser may block autoplay)
        })
      },
    }),
    {
      name: 'bat-monitoring-storage',
      partialize: (state) => ({
        autoRefreshEnabled: state.autoRefreshEnabled,
        autoRefreshInterval: state.autoRefreshInterval,
        soundNotifications: state.soundNotifications,
      }),
    },
  ),
)
